import { useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: OverrideReason) => void;
  scenario: 'early-pickup' | 'rollback' | 'mistaken-pickup';
  orderNumber?: string;
}

export interface OverrideReason {
  scenario: 'early-pickup' | 'rollback' | 'mistaken-pickup';
  reasonCode: string;
  evidenceNote: string;
  approvedBy: string;
  acknowledgeRisk: boolean;
}

const REASON_OPTIONS: Record<string, { value: string; label: string }[]> = {
  'early-pickup': [
    { value: 'customer-urgent', label: '客戶急件（拍攝提前）' },
    { value: 'equipment-ready', label: '器材已備妥且無排程衝突' },
    { value: 'manager-discretion', label: '店長裁量（VIP / 長期客戶）' },
    { value: 'force-majeure', label: '不可抗力調度' },
  ],
  'rollback': [
    { value: 'customer-request', label: '客戶主動要求取消/改期' },
    { value: 'equipment-failure', label: '器材臨時故障無法出件' },
    { value: 'payment-issue', label: '款項爭議未解' },
    { value: 'fraud-detected', label: '發現異常（詐騙/盜用）' },
  ],
  'mistaken-pickup': [
    { value: 'wrong-equipment', label: '錯發器材（已追回）' },
    { value: 'wrong-customer', label: '身份誤認（非本人領取）' },
    { value: 'system-error', label: '系統誤標為已取件' },
  ],
};

const SCENARIO_LABELS: Record<string, string> = {
  'early-pickup': '提前取件核准',
  'rollback': '狀態回退覆寫',
  'mistaken-pickup': '誤取件更正',
};

export default function ManagerOverrideModal({ isOpen, onClose, onConfirm, scenario, orderNumber }: Props) {
  const [reasonCode, setReasonCode] = useState('');
  const [evidenceNote, setEvidenceNote] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const [acknowledgeRisk, setAcknowledgeRisk] = useState(false);

  if (!isOpen) return null;

  const options = REASON_OPTIONS[scenario] || [];
  const canSubmit = reasonCode && evidenceNote.trim().length >= 5 && approvedBy.trim().length >= 2 && acknowledgeRisk;

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm({
      scenario,
      reasonCode,
      evidenceNote: evidenceNote.trim(),
      approvedBy: approvedBy.trim(),
      acknowledgeRisk,
    });
    // reset
    setReasonCode('');
    setEvidenceNote('');
    setApprovedBy('');
    setAcknowledgeRisk(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <i className="ri-shield-keyhole-line text-2xl text-amber-600"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{SCENARIO_LABELS[scenario]}</h3>
            <p className="text-sm text-gray-500">訂單編號：{orderNumber || '-'}</p>
          </div>
        </div>

        <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-start gap-2 text-sm text-amber-800">
            <i className="ri-error-warning-line mt-0.5"></i>
            <div>
              <p className="font-medium">此操作將繞過一般流程限制</p>
              <p className="text-xs text-amber-600 mt-1">
                覆寫紀錄將被記錄於 Audit Trail（目前為 Mock / Pending backend connection）。
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              覆寫原因 <span className="text-red-500">*</span>
            </label>
            <select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">請選擇原因</option>
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              佐證說明 / 備註 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={evidenceNote}
              onChange={(e) => setEvidenceNote(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="請說明覆寫理由與佐證資訊，例如：客戶電話確認紀錄、店長口頭授權等..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
            <div className="text-xs text-gray-400 mt-1">{evidenceNote.length} / 500 字</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              核准人（姓名 / 員工編號） <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={approvedBy}
              onChange={(e) => setApprovedBy(e.target.value)}
              placeholder="輸入核准人姓名"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledgeRisk}
              onChange={(e) => setAcknowledgeRisk(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">
              本人確認此覆寫已了解相關法律與財務風險，並願意承擔責任。
            </span>
          </label>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className={`flex-1 px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
              canSubmit
                ? 'bg-amber-600 text-white hover:bg-amber-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            確認覆寫
          </button>
        </div>
      </div>
    </div>
  );
}

export { SCENARIO_LABELS };